import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { useAdminCms, saveCmsSection, type AdminCmsSection } from '../../api/cms';

const SECTION_LABEL: Record<string, string> = {
  'company.hero': 'itcitysoft.com — hero',
  'product.hero': 'possignin.com — hero',
  'product.announcement': 'possignin.com — announcement bar',
  'pricing.intro': 'Pricing — intro',
};
const FIELD_LABEL: Record<string, string> = {
  eyebrow: 'Eyebrow', headline: 'Headline', subhead: 'Subhead', text: 'Text', href: 'Link',
};

/**
 * Marketing CMS editor (platform admin) — §WS5b. A structured editor over the
 * marketing_content sections; each section is a flat map of string fields shown
 * as labelled textareas. Saving invalidates the public `cms` query so the live
 * marketing pages pick up the change. No raw HTML, no page builder.
 */
export function Cms() {
  const { data: sections = [], isLoading } = useAdminCms();

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Marketing content"
        subtitle="Edit the public marketing pages (itcitysoft.com / possignin.com). Changes go live immediately."
      />
      {isLoading ? (
        <Card><div className="p-6 space-y-3" aria-busy="true"><div className="skeleton h-5 w-40 rounded" /><div className="skeleton h-24 rounded" /><div className="skeleton h-24 rounded" /></div></Card>
      ) : sections.length === 0 ? (
        <Card><div className="p-6 text-sm text-zinc-500 dark:text-zinc-400">No content yet — run <code className="mono">db:seed-cms</code> to seed the sections.</div></Card>
      ) : (
        <div className="space-y-5">
          {sections.map((s) => (
            <SectionEditor key={s.sectionKey} section={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionEditor({ section }: { section: AdminCmsSection }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>(section.value);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(section.value);

  async function save() {
    setBusy(true);
    setSaved(false);
    try {
      await saveCmsSection(section.sectionKey, draft);
      await qc.invalidateQueries({ queryKey: ['admin-cms'] });
      await qc.invalidateQueries({ queryKey: ['cms'] });
      setSaved(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold text-zinc-950 dark:text-zinc-50">{SECTION_LABEL[section.sectionKey] ?? section.sectionKey}</h2>
        <span className="text-xs text-zinc-400 dark:text-zinc-500 mono">{section.sectionKey}</span>
      </div>
      <div className="mt-4 space-y-3">
        {Object.entries(draft).map(([field, value]) => (
          <label key={field} className="block">
            <span className="block text-xs font-medium text-zinc-600 dark:text-zinc-300 mb-1">{FIELD_LABEL[field] ?? field}</span>
            <textarea
              rows={field === 'subhead' || field === 'text' ? 2 : 1}
              value={value}
              onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
              className="w-full px-3 py-2 rounded-md text-sm border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 focus:ring-2 focus:ring-brand/40 focus:border-brand resize-y"
            />
          </label>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={save} disabled={busy || !dirty} iconLeft={<Save className="w-4 h-4" />}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        {saved && !dirty && <span className="text-sm text-emerald-600 dark:text-emerald-400">Saved ✓</span>}
      </div>
    </Card>
  );
}
